import Auth from '@aws-amplify/auth'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Typography } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import { TFunction } from 'i18next'
import * as moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { withTranslation } from 'react-i18next'
import {
  AWS_AUTH_RETRY_CONFIG,
  completeSignUpAuth,
  establishSignInAuth,
  getAuthAccessToken,
  getUserIdFromAuth,
  resolveSignInSession,
} from '../../../../utils/completeSignUpAuth'
import {
  sendExtensionMessage,
  sendToExtension,
} from '../../../../utils/extensionHelper'
import { callWithRetry } from '../../../../utils/retry'
import { setItemToLocalStorage } from '../../../../utils/setLocalStorage'
import {
  getOtpVerificationErrorMessage,
  getVerifyContinueErrorMessage,
} from '../../../../utils/signupErrors'
import {
  clearSignupOtpSessionStorage,
  formatResendTimer,
  getOtpVerifyRateLimitMessage,
  getRemainingCooldownSeconds,
  OtpSendRateLimitData,
  persistOtpSendBlocked,
  persistOtpSendSuccess,
  restoreOtpResendFromSessionStorage,
  SIGNUP_OTP_CLEANUP_KEYS,
  SIGNUP_OTP_RESEND_KEYS,
} from '../../../../utils/signupOtpResendBackoff'
import { TrackingCase } from '../../../../utils/trackingCases'
import { buildFinancialData } from '../../../../utils/financialData'
import {
  checkIfSubmittable,
  getEmailDomainBlockedMessage,
  isEmailDomainBlockedError,
  validateEmailDomain,
  validation,
} from '../../../../utils/validation'
import { useAppState } from '../../../appState'
import closeMenuIcon from '../../../assets/images/close.svg'
import emailIcon from '../../../assets/images/email.svg'
import axios from '../../../axios'
import { ENDPOINTS, EXTENSION_ID, ROUTES } from '../../../constants'
import useBrandJoinFlow from '../../../hooks/useBrandJoinFlow'
import { useDeferredCallbackWhenReady } from '../../../hooks/useDeferredCallbackWhenReady'
import useReCaptcha from '../../../hooks/useReCaptcha'
import { tracker } from '../../../systemLogs'
import IOSSwitch from '../../IOSSwitch'
import CHRButton from '../../button'
import CHRInput from '../../input'
import { styles } from './styles'
declare const window: any

const eye = <FontAwesomeIcon icon={faEye} />
const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />

interface MainJoinModalProps {
  handleClose: any
  openStatus: boolean
  isExtensionDownloaded: boolean
  handleFinish: Function
  redirectToLogin: Function
  isTermsCheckedParent: boolean
  isNewsletterCheckedParent: boolean
  t: TFunction
}

const MainJoinModal = ({
  handleClose,
  openStatus,
  isExtensionDownloaded,
  handleFinish,
  isTermsCheckedParent,
  isNewsletterCheckedParent,
  redirectToLogin,
  t,
}: MainJoinModalProps) => {
  const classes = styles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isTermsChecked, setIsTermsChecked] = useState(true)
  const [isNewsletterChecked, setIsNewsletterChecked] = useState(true)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const [passwordShown, setPasswordShown] = useState(false)
  const [formErrors, setFormErrors] = useState<{
    emailValidationError?: boolean
    emailErrorText?: string
    passwordValidationError?: boolean
    passwordErrorText?: string
    requestError: boolean
    requestErrorText: string
  }>({
    emailValidationError: false,
    emailErrorText: '',
    passwordValidationError: false,
    passwordErrorText: '',
    requestError: false,
    requestErrorText: '',
  })
  const [state, dispatch] = useAppState()
  const { pathname } = useLocation()
  const referralId = pathname?.split('/')[2]?.split('#')[0] || ''
  const timer = useRef(null)
  const [signupStep, setSignupStep] = useState<'form' | 'verify'>('form')
  const [otpValue, setOtpValue] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpInfo, setOtpInfo] = useState('')
  const [resendCooldown, setResendCooldown] = useState(60)
  const [resendTimer, setResendTimer] = useState(60)
  const [resendEnabled, setResendEnabled] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null)
  const [isUnverifiedResignup, setIsUnverifiedResignup] = useState(false)
  const [showSignInLink, setShowSignInLink] = useState(false)
  const otpInputRef = useRef<HTMLInputElement>(null)
  const pendingUserData = useRef<any>(null)
  const [storageReady, setStorageReady] = useState(false)

  const startCountdown = (seconds: number) => {
    persistOtpSendSuccess(seconds)
    setIsBlocked(false)
    setBlockedUntil(null)
    setResendCooldown(seconds)
    setResendTimer(seconds)
    setResendEnabled(false)
  }

  const handleOtpSendRateLimit = (data: OtpSendRateLimitData) => {
    if (data.isBlocked && data.blockedUntil) {
      const blocked = new Date(data.blockedUntil)
      setIsBlocked(true)
      setBlockedUntil(blocked)
      setResendEnabled(false)
      persistOtpSendBlocked(blocked)
      return
    }

    const remaining = getRemainingCooldownSeconds(data)
    if (remaining !== null && remaining > 0) {
      startCountdown(remaining)
    }
  }

  useEffect(() => {
    if (!openStatus) {
      setStorageReady(false)
      return
    }
    const restored = restoreOtpResendFromSessionStorage()
    if (restored.email !== undefined) setEmail(restored.email)
    if (restored.password !== undefined) setPassword(restored.password)
    if (restored.signupStep !== undefined) setSignupStep(restored.signupStep)
    if (restored.isBlocked) {
      setIsBlocked(true)
      setBlockedUntil(restored.blockedUntil ?? null)
    } else {
      if (restored.resendTimer !== undefined)
        setResendTimer(restored.resendTimer)
      if (restored.resendEnabled !== undefined) {
        setResendEnabled(restored.resendEnabled)
      }
    }
    setStorageReady(true)
  }, [openStatus])

  useEffect(() => {
    if (!storageReady) return
    sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.email, email)
  }, [email, storageReady])

  useEffect(() => {
    if (!storageReady) return
    sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.password, password)
  }, [password, storageReady])

  useEffect(() => {
    if (!storageReady) return
    sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.step, signupStep)
  }, [signupStep, storageReady])

  useEffect(() => {
    if (!storageReady) return
    sessionStorage.setItem(
      SIGNUP_OTP_RESEND_KEYS.resendTimer,
      String(resendTimer)
    )
  }, [resendTimer, storageReady])

  useEffect(() => {
    if (!storageReady) return
    sessionStorage.setItem(
      SIGNUP_OTP_RESEND_KEYS.resendEnabled,
      String(resendEnabled)
    )
  }, [resendEnabled, storageReady])

  useEffect(() => {
    if (signupStep === 'verify') {
      otpInputRef.current?.focus()
    }
  }, [signupStep])

  useEffect(() => {
    if (signupStep !== 'verify' || resendEnabled || isBlocked) return
    if (resendTimer <= 0) {
      setResendEnabled(true)
      return
    }
    const id = setTimeout(() => setResendTimer(t => t - 1), 1000)
    return () => clearTimeout(id)
  }, [signupStep, resendTimer, resendEnabled, isBlocked])

  const {
    joinFlowBrandName,
    generateBrandTrackingLinkForUser,
    getBrandDetailsFromStorage,
  } = useBrandJoinFlow()

  const {
    captchaRef,
    isVerifiedFromClient,
    isClientVerificationInProgress,
    isVerifiedFromServer,
    isServerVerificationInProgress,
    onExpiry,
    onVerify,
  } = useReCaptcha({
    customErrorSetter: (message: string) => {
      setFormErrors({
        requestError: message.length > 0,
        requestErrorText: message,
      })
    },
  })

  useEffect(() => {
    setIsTermsChecked(isTermsCheckedParent)
    setIsNewsletterChecked(isNewsletterCheckedParent)
  }, [isTermsCheckedParent, isNewsletterCheckedParent])

  const handleAutoSignIn = async (email: string, password: string) => {
    setIsFormSubmitting(true)

    // Check for restricted domains
    const domainValidation = await validateEmailDomain(email, 'signin')
    if (!domainValidation.isValid) {
      setFormErrors({
        requestError: true,
        requestErrorText:
          domainValidation.errorMessage || 'Invalid email domain',
      })
      setIsFormSubmitting(false)
      return
    }

    try {
      const normalizedEmail = email.trim().toLowerCase()
      const { data: signInResponse } = await axios.post(ENDPOINTS.signIn, {
        email: normalizedEmail,
        password,
      })
      const { user, authPayload } = await establishSignInAuth(
        signInResponse?.data,
        normalizedEmail,
        password,
        AWS_AUTH_RETRY_CONFIG
      )
      const { accessToken, session, userId } = await resolveSignInSession(
        user,
        authPayload,
        AWS_AUTH_RETRY_CONFIG
      )

      if (!session && !accessToken) {
        throw new Error(t('messages.notAuthorized'))
      }

      const {
        data: { data },
      } = await axios.get(
        ENDPOINTS.user.replace(':id', user.attributes['custom:user_id'])
      )
      const currentUserInfo = await Auth.currentCredentials()
      const userProfile = {
        email: data.email,
        userName: data.username,
        fullName: data.name,
        imageKey: data.image,
        imageUrl: '' as Object | string,
        isPro: data.isPro,
        isCreator: data.isCreator,
        isReferralBonus: data.isReferralBonus,
        userDoublePayment: data.userDoublePayment,
        financialData: buildFinancialData(data.financialData),
        loginProvider: data.loginProvider,
        isShortLinkReferralAllowed: data.isShortLinkReferralAllowed,
        shareLinkSegment: data.shareLinkSegment,
        shareLinkDeclaredAt: data.shareLinkDeclaredAt,
        shareLinkReviewStatus: data.shareLinkReviewStatus,
        canCreateForwardableBoards: data.canCreateForwardableBoards || false,
        canCreateEditPage: data.canCreateEditPage || false,
        defaultShoppingBoardView: data.defaultShoppingBoardView,
      }

      if (data.image && currentUserInfo?.identityId) {
        const url = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${data.image}`
        userProfile.imageUrl = url
      }

      sendExtensionMessage({ session })
      sendToExtension('CHR_AUTH_SESSION', { auth: session })

      if (window.chrome?.runtime?.sendMessage) {
        window.chrome.runtime.sendMessage(
          EXTENSION_ID,
          { session },
          _response => {}
        )
      }

      dispatch({ type: 'SIGN_IN', auth: user, userProfile })
      SIGNUP_OTP_CLEANUP_KEYS.forEach(key => sessionStorage.removeItem(key))

      const urlParams = new URLSearchParams(window.location.search)
      const signInFromExtension = urlParams.get('signInFromExtension')

      navigate(
        `${ROUTES.member}${
          signInFromExtension ? '?signInFromExtension=signInFromExtension' : ''
        }`
      )
    } catch (err) {
      setFormErrors({
        requestError: true,
        requestErrorText: 'An account with this email already exists',
      })
    }
    setIsFormSubmitting(false)
  }

  const handleSignUp = async () => {
    setIsFormSubmitting(true)
    setFormErrors({
      emailValidationError: false,
      emailErrorText: '',
      passwordValidationError: false,
      passwordErrorText: '',
      requestError: false,
      requestErrorText: '',
    })
    setShowSignInLink(false)

    // Check for restricted domains
    const domainValidation = await validateEmailDomain(email, 'signup')
    if (!domainValidation.isValid) {
      setFormErrors({
        emailValidationError: true,
        emailErrorText: domainValidation.errorMessage || 'Invalid email domain',
        passwordValidationError: false,
        passwordErrorText: '',
        requestError: false,
        requestErrorText: '',
      })
      setIsFormSubmitting(false)
      return
    }

    const isCaptchaVerified = isVerifiedFromClient && isVerifiedFromServer

    try {
      const isFormValid = await validation({ email, password })
      if (isTermsChecked && isFormValid && isCaptchaVerified) {
        let userData = {
          email,
          password,
          termsCondsAccepted: isTermsChecked,
          newsletterSubscription: isNewsletterChecked,
          extensionDownloaded: isExtensionDownloaded,
        }
        if (referralId) {
          Object.assign(userData, { referral: referralId })
        } else {
          const referralIdFallBackJSON = window.localStorage.getItem(
            'referralId'
          )
          const referralIdFallBack = JSON.parse(referralIdFallBackJSON)
          if (referralIdFallBack) {
            const { id, time } = referralIdFallBack
            const referralIdAge = moment().diff(moment(time), 'days')
            if (referralIdAge <= 3) {
              Object.assign(userData, { referral: id })
            }
          }
        }

        const brand = getBrandDetailsFromStorage()
        if (brand.referrerId) {
          Object.assign(userData, {
            refBrandId: String(brand.brandId),
            refUserId: Number(brand.referrerId),
          })
        }
        pendingUserData.current = userData
        tracker.track(
          TrackingCase.UserTracking,
          `POST ${ENDPOINTS.sendVerificationOtp}`,
          {
            location: 'MainJoinModal',
            email,
          }
        )
        const { data } = await axios.post(ENDPOINTS.sendVerificationOtp, {
          email,
        })
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.email, email)
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.password, password)
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.step, 'verify')
        setOtpValue('')
        setOtpError('')
        setOtpInfo('')
        startCountdown(data?.cooldownSeconds ?? 60)
        setIsUnverifiedResignup(data?.isUnverified === true)
        setSignupStep('verify')
      }
    } catch (error) {
      const responseError = (error as {
        response?: { status?: number; data?: any }
      })?.response

      if (isEmailDomainBlockedError(error)) {
        setFormErrors({
          emailValidationError: true,
          emailErrorText: getEmailDomainBlockedMessage(error),
          passwordValidationError: false,
          passwordErrorText: '',
          requestError: false,
          requestErrorText: '',
        })
        setIsFormSubmitting(false)
        return
      }

      if (responseError?.status === 429 && responseError.data) {
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.email, email)
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.password, password)
        sessionStorage.setItem(SIGNUP_OTP_RESEND_KEYS.step, 'verify')
        setSignupStep('verify')
        handleOtpSendRateLimit(responseError.data)
        setIsFormSubmitting(false)
        return
      }

      if (error.errors) {
        if (error.inner[0]?.params.path === 'email') {
          setFormErrors({
            emailValidationError: true,
            emailErrorText: error.inner[0]?.message,
            passwordValidationError:
              error.inner[1]?.params?.path === 'password',
            passwordErrorText: error.inner[1]?.message,
          })
        } else {
          setFormErrors({
            passwordErrorText: error.inner[0]?.message,
            passwordValidationError:
              error.inner[0]?.params?.path === 'password',
          })
        }
      } else {
        let errMsg = t('join.errorCreatingANewAccount')
        if (responseError) {
          const loginProvider =
            responseError.data.loginProvider?.toLowerCase() || ''
          const socialProvider = loginProvider.includes('google')
            ? 'Google'
            : 'Apple'
          const isConflict = responseError.status === 409
          const isSocialProvider =
            loginProvider.includes('google') || loginProvider.includes('apple')

          if (isConflict && responseError.data?.isVerified === true) {
            errMsg =
              responseError.data?.message ||
              'An account with this email already exists'
            setShowSignInLink(true)
          } else if (isConflict && isSocialProvider) {
            errMsg = `You have already singed in with this email with ${socialProvider}`
            timer.current = setTimeout(() => {
              redirectToLogin()
              onDialogClose()
            }, 3000)
          } else if (isConflict) {
            return handleAutoSignIn(email, password)
          }
        }

        setFormErrors({
          requestError: true,
          requestErrorText: errMsg,
        })
      }
    }

    setIsFormSubmitting(false)
  }

  const handleSignInLinkClick = () => {
    onDialogClose()
    redirectToLogin()
  }

  const handleCreateAccount = async () => {
    setIsFormSubmitting(true)
    setOtpError('')

    if (!otpValue || otpValue.length !== 6) {
      setOtpError('Please enter the 6-digit code.')
      setIsFormSubmitting(false)
      return
    }

    try {
      const { data: verifyData } = await axios.post(ENDPOINTS.verifyEmailOtp, {
        email,
        otp: otpValue,
      })

      const userData = pendingUserData.current || {
        email,
        password,
        termsCondsAccepted: isTermsChecked,
        newsletterSubscription: isNewsletterChecked,
        extensionDownloaded: isExtensionDownloaded,
      }

      if (verifyData?.requiresCognitoVerification) {
        await axios.post(ENDPOINTS.verifyCognitoEmail, { email, password })
      }

      const awsRetryConfig = {
        timeout: 5 * 1000,
        maxRetries: 3,
        waitBetweenTries: 2 * 1000,
      }
      const { data: signUpResponse } = await axios.post(
        ENDPOINTS.signUp,
        userData
      )

      const signUpAuthPayload = signUpResponse?.data
      const user = await completeSignUpAuth(
        signUpAuthPayload,
        userData.email,
        userData.password,
        awsRetryConfig
      )

      setItemToLocalStorage('signUp', 'firstVisit')
      setItemToLocalStorage('firstMove', 'true')

      if (user) {
        const accessToken = await getAuthAccessToken(user, signUpAuthPayload)
        const currentSession = await callWithRetry(
          () => Auth.currentSession(),
          awsRetryConfig
        )

        if (!currentSession && !accessToken) {
          navigate(ROUTES.signIn)
        } else {
          const userId = getUserIdFromAuth(user, signUpAuthPayload)
          const {
            data: { data },
          } = await axios.get(ENDPOINTS.user.replace(':id', userId), {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
          const userProfile = {
            email: data.email,
            userName: data.username,
            fullName: data.name,
            imageKey: data.image,
            imageUrl: '' as Object | string,
            isPro: data.isPro,
            isCreator: data.isCreator,
            isReferralBonus: data.isReferralBonus,
            userDoublePayment: data.userDoublePayment,
            financialData: buildFinancialData(data.financialData),
            loginProvider: data.loginProvider,
            isShortLinkReferralAllowed: data.isShortLinkReferralAllowed,
            shareLinkSegment: data.shareLinkSegment,
            shareLinkDeclaredAt: data.shareLinkDeclaredAt,
            shareLinkReviewStatus: data.shareLinkReviewStatus,
            canCreateForwardableBoards:
              data.canCreateForwardableBoards || false,
            canCreateEditPage: data.canCreateEditPage || false,
            defaultShoppingBoardView: data.defaultShoppingBoardView,
          }

          if (data.image && currentSession?.identityId) {
            const url = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${data.image}`
            userProfile.imageUrl = url
          }
          sendExtensionMessage({ session: currentSession })
          sendToExtension('CHR_AUTH_SESSION', { auth: currentSession })
          if (window.chrome?.runtime?.sendMessage) {
            window.chrome.runtime.sendMessage(
              EXTENSION_ID,
              { session: currentSession },
              _response => {}
            )
          }

          dispatch({ type: 'SIGN_IN', auth: user, userProfile })
          clearSignupOtpSessionStorage()
          handleFinish()
        }
      }
    } catch (error) {
      const responseError = error.response

      if (responseError?.status === 400) {
        setOtpError(
          getOtpVerificationErrorMessage(responseError?.data?.message)
        )
      } else if (responseError?.status === 429) {
        if (responseError.data) {
          handleOtpSendRateLimit(responseError.data)
        }
        setOtpError(getOtpVerifyRateLimitMessage(responseError.data))
      } else {
        const loginProvider =
          responseError?.data?.loginProvider?.toLowerCase() || ''
        const isConflict = responseError?.status === 409
        const isSocialProvider =
          loginProvider.includes('google') || loginProvider.includes('apple')

        if (isConflict && !isSocialProvider && !isUnverifiedResignup) {
          setIsFormSubmitting(false)
          return handleAutoSignIn(email, password)
        }

        let errMsg = getVerifyContinueErrorMessage(
          error,
          t('join.errorCreatingANewAccount')
        )
        if (isConflict && isUnverifiedResignup) {
          errMsg =
            'Unable to complete registration. Please try again or contact support.'
        }
        if (isConflict && isSocialProvider) {
          const socialProvider = loginProvider.includes('google')
            ? 'Google'
            : 'Apple'
          errMsg = `You have already singed in with this email with ${socialProvider}`
        }
        setOtpError(errMsg)
        window.Sentry?.captureException(error?.response?.data?.message || error)
      }
    }

    setIsFormSubmitting(false)
  }

  const handleResendOtp = async () => {
    if (!resendEnabled || isBlocked) return
    setOtpError('')
    setOtpInfo('')
    setIsFormSubmitting(true)
    try {
      const { data } = await axios.post(ENDPOINTS.sendVerificationOtp, {
        email,
      })
      setOtpValue('')
      startCountdown(data?.cooldownSeconds ?? resendCooldown)
      setOtpInfo('A new code has been sent to your email.')
    } catch (error) {
      if (error.response?.status === 429 && error.response.data) {
        handleOtpSendRateLimit(error.response.data)
        setOtpError('Too many attempts. Please try again after 24 hours.')
      } else {
        setOtpError('Could not resend the code. Please try again.')
      }
    }
    setIsFormSubmitting(false)
  }

  const handleBackToForm = () => {
    clearSignupOtpSessionStorage()
    setSignupStep('form')
    setOtpValue('')
    setOtpError('')
    setOtpInfo('')
    setResendCooldown(60)
    setResendTimer(60)
    setResendEnabled(false)
    setIsBlocked(false)
    setBlockedUntil(null)
    setIsUnverifiedResignup(false)
  }

  const { trigger: triggerSignUp, isWaiting } = useDeferredCallbackWhenReady(
    [() => isServerVerificationInProgress === false],
    handleSignUp
  )

  const isSubmitting = isFormSubmitting || isWaiting

  const isJoinBtnDisabled =
    isSubmitting ||
    isClientVerificationInProgress ||
    !isVerifiedFromClient ||
    !email ||
    !password

  const handleNewslettersSwitch = () => {
    setIsNewsletterChecked(!isNewsletterChecked)
  }

  const handleTermsSwitch = () => {
    setIsTermsChecked(!isTermsChecked)
  }

  const togglePasswordVisibility = () => {
    setPasswordShown(!passwordShown)
  }

  const onDialogClose = () => {
    // if the user closed the join modal then we don't show the sign in one
    clearTimeout(timer.current)

    // we don't want users to close the modal while it is submitting
    if (isSubmitting) return

    setFormErrors({
      emailValidationError: false,
      emailErrorText: '',
      passwordValidationError: false,
      passwordErrorText: '',
      requestError: false,
      requestErrorText: '',
    })
    setOtpValue('')
    setOtpError('')
    setOtpInfo('')
    setIsUnverifiedResignup(false)
    setShowSignInLink(false)

    handleClose()

    if (joinFlowBrandName) {
      window.location.replace(
        generateBrandTrackingLinkForUser(
          getBrandDetailsFromStorage(),
          { userId: '0', username: '' },
          false
        )
      )
    }
  }

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={openStatus}
        onClose={onDialogClose}
        aria-labelledby="joinWithEmail"
        className={classes.modal}
        maxWidth="md"
      >
        <div className={classes.modalContainer}>
          <button onClick={onDialogClose} className={classes.closeButton}>
            <img src={closeMenuIcon} alt={t('imageAlts.close')} />
          </button>
          <Typography variant="h2" className={classes.modalTitle}>
            join chirpyest
          </Typography>
          <Typography variant="subtitle1">
            cash back from 1000s of retailers and top brands. join to shop,
            share and earn $$!
          </Typography>
          <form className={classes.container}>
            <p className={classes.error}>
              {formErrors.emailValidationError && formErrors.emailErrorText}
            </p>
            <CHRInput
              value={email}
              onChange={e => {
                setEmail(e.target.value)
              }}
              placeholder={t('shared.emailAddress')}
              type="email"
              error={formErrors.emailValidationError}
              required
              disabled={signupStep === 'verify'}
              onKeyPress={event =>
                !isSubmitting && checkIfSubmittable(event, triggerSignUp)
              }
              autoComplete="new-password"
            />
            <p className={classes.error}>
              {formErrors.passwordValidationError &&
                formErrors.passwordErrorText}
            </p>
            <div className={classes.passwordField}>
              <CHRInput
                value={password}
                onChange={e => {
                  setPassword(e.target.value)
                }}
                placeholder={t('shared.password')}
                type={passwordShown ? 'text' : 'password'}
                error={formErrors.passwordValidationError}
                required
                disabled={signupStep === 'verify'}
                onKeyPress={event =>
                  !isSubmitting && checkIfSubmittable(event, triggerSignUp)
                }
                autoComplete="new-password"
              />
              <i
                className={classes.passVisibility}
                onClick={togglePasswordVisibility}
              >
                {passwordShown ? eye : eyeSlash}
              </i>
            </div>
            {signupStep === 'form' && (
              <p className={classes.passwordGuidelines}>
                {t('join.passwordGuidelines')}
              </p>
            )}

            {signupStep === 'verify' && (
              <>
                <p className={classes.switchLabel} style={{ marginTop: 8 }}>
                  {isUnverifiedResignup
                    ? `You previously signed up but didn't verify. We sent a new code to ${email}.`
                    : `We sent a 6-digit code to ${email}.`}{' '}
                  <span
                    className={classes.bold}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={handleBackToForm}
                  >
                    Change email
                  </span>
                </p>
                <CHRInput
                  value={otpValue}
                  onChange={e =>
                    setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="6-digit code"
                  type="text"
                  elementRef={otpInputRef}
                  onKeyPress={event =>
                    !isSubmitting &&
                    checkIfSubmittable(event, handleCreateAccount)
                  }
                  autoComplete="one-time-code"
                />
                {otpError && <p className={classes.error}>{otpError}</p>}
                {otpInfo && (
                  <p
                    className={classes.switchLabel}
                    style={{ color: 'green', marginTop: 4 }}
                  >
                    {otpInfo}
                  </p>
                )}
                <p
                  className={classes.switchLabel}
                  style={{ textAlign: 'center', marginTop: 8 }}
                >
                  {resendEnabled ? (
                    <span
                      className={classes.bold}
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                      onClick={handleResendOtp}
                    >
                      Resend code
                    </span>
                  ) : (
                    <span style={{ opacity: 0.5 }}>
                      Resend code ({formatResendTimer(resendTimer)})
                    </span>
                  )}
                </p>
              </>
            )}

            <p className={classes.error}>
              {formErrors.requestError && formErrors.requestErrorText}
            </p>
            {showSignInLink && (
              <p className={classes.switchLabel} style={{ marginTop: 4 }}>
                <span
                  className={classes.bold}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={handleSignInLinkClick}
                >
                  Already have an account? Sign in
                </span>
              </p>
            )}
            {signupStep === 'form' && (
              <ReCAPTCHA
                sitekey={String(process.env.GATSBY_RECAPTCHA_SECRET_KEY)}
                onChange={onVerify}
                onExpired={onExpiry}
                ref={captchaRef}
              />
            )}
            {signupStep === 'form' ? (
              <CHRButton
                onClick={triggerSignUp}
                label={
                  <Box pl={joinFlowBrandName ? 4 : 0}>
                    <Box>
                      {'Continue'}
                      {joinFlowBrandName ? ' to' : ''}
                    </Box>
                    <Box>{joinFlowBrandName}</Box>
                  </Box>
                }
                icon={emailIcon}
                isSubmitting={isSubmitting}
                disabled={isJoinBtnDisabled}
                customStyle={classes.buttonStyles}
              />
            ) : (
              <CHRButton
                onClick={handleCreateAccount}
                label={'Verify & Create Account'}
                icon={emailIcon}
                isSubmitting={isSubmitting}
                disabled={isSubmitting || otpValue.length !== 6}
                customStyle={classes.buttonStyles}
              />
            )}
            {signupStep === 'form' && (
              <div className={classes.switches}>
                <p className={classes.error}>
                  {!isTermsChecked && t('join.termsAndConditions')}
                </p>
                <div className={classes.termsWrapper}>
                  <IOSSwitch
                    checked={isTermsChecked}
                    onChange={handleTermsSwitch}
                    name="terms"
                  />
                  <p className={classes.switchLabel}>
                    {t('join.accept')}{' '}
                    <span className={classes.bold}>
                      <a
                        href={ROUTES.terms}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#000', textDecoration: 'none' }}
                      >
                        {t('join.termsAndPrivacy')}
                      </a>
                    </span>
                  </p>
                </div>
                <div className={classes.termsWrapper}>
                  <IOSSwitch
                    checked={isNewsletterChecked}
                    onChange={handleNewslettersSwitch}
                    name="newsletters"
                  />
                  <p className={classes.switchLabel}>
                    {t('join.subscribeToNewsletter')}
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </Dialog>
    </>
  )
}

export default withTranslation()(MainJoinModal)
